import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type {
  OrderListParams,
  OrderStatus,
  OrderStatusFilter,
  PaymentMethod,
  PaymentMethodFilter,
  PaymentStatus,
  PaymentStatusFilter,
} from '@/types/order';

export interface OrderQueryState {
  page: number;
  limit: number;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  search: string;
  customerId: string;
  orderStatus: OrderStatusFilter;
  paymentStatus: PaymentStatusFilter;
  paymentMethod: PaymentMethodFilter;
  dateFrom: string;
  dateTo: string;
}

type ParamPatch = Partial<{
  page: number;
  limit: number;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  search: string | null;
  customerId: string | null;
  orderStatus: OrderStatusFilter | null;
  paymentStatus: PaymentStatusFilter | null;
  paymentMethod: PaymentMethodFilter | null;
  dateFrom: string | null;
  dateTo: string | null;
}>;

const DEFAULTS: OrderQueryState = {
  page: 1,
  limit: 10,
  sortBy: 'placedAt',
  sortOrder: 'desc',
  search: '',
  customerId: '',
  orderStatus: 'all',
  paymentStatus: 'all',
  paymentMethod: 'all',
  dateFrom: '',
  dateTo: '',
};

function parseOrderStatus(value: string | null): OrderStatusFilter {
  const allowed: OrderStatus[] = [
    'pending',
    'confirmed',
    'processing',
    'packed',
    'creating',
    'shipped',
    'delivered',
    'installed',
    'closed',
    'cancelled',
    'returned',
  ];
  return allowed.includes(value as OrderStatus) ? (value as OrderStatus) : 'all';
}

function parsePaymentStatus(value: string | null): PaymentStatusFilter {
  const allowed: PaymentStatus[] = ['pending', 'paid', 'failed', 'refunded'];
  return allowed.includes(value as PaymentStatus) ? (value as PaymentStatus) : 'all';
}

function parsePaymentMethod(value: string | null): PaymentMethodFilter {
  const allowed: PaymentMethod[] = ['cod', 'razorpay', 'stripe', 'bank_transfer'];
  return allowed.includes(value as PaymentMethod) ? (value as PaymentMethod) : 'all';
}

export function useOrderQueryParams() {
  const [searchParams, setSearchParams] = useSearchParams();

  const state = useMemo<OrderQueryState>(() => {
    const page = Number(searchParams.get('page') || DEFAULTS.page);
    const limit = Number(searchParams.get('limit') || DEFAULTS.limit);

    return {
      page: Number.isFinite(page) && page > 0 ? page : DEFAULTS.page,
      limit: Number.isFinite(limit) && limit > 0 ? Math.min(limit, 100) : DEFAULTS.limit,
      sortBy: searchParams.get('sortBy') || DEFAULTS.sortBy,
      sortOrder: searchParams.get('sortOrder') === 'asc' ? 'asc' : 'desc',
      search: searchParams.get('search') || '',
      customerId: searchParams.get('customerId') || '',
      orderStatus: parseOrderStatus(searchParams.get('orderStatus')),
      paymentStatus: parsePaymentStatus(searchParams.get('paymentStatus')),
      paymentMethod: parsePaymentMethod(searchParams.get('paymentMethod')),
      dateFrom: searchParams.get('dateFrom') || '',
      dateTo: searchParams.get('dateTo') || '',
    };
  }, [searchParams]);

  const apiParams = useMemo<OrderListParams>(
    () => ({
      page: state.page,
      limit: state.limit,
      sortBy: state.sortBy,
      sortOrder: state.sortOrder,
      ...(state.search ? { search: state.search } : {}),
      ...(state.customerId ? { customerId: state.customerId } : {}),
      ...(state.orderStatus !== 'all' ? { orderStatus: state.orderStatus } : {}),
      ...(state.paymentStatus !== 'all' ? { paymentStatus: state.paymentStatus } : {}),
      ...(state.paymentMethod !== 'all' ? { paymentMethod: state.paymentMethod } : {}),
      ...(state.dateFrom ? { dateFrom: state.dateFrom } : {}),
      ...(state.dateTo ? { dateTo: state.dateTo } : {}),
    }),
    [state],
  );

  const patchParams = useCallback(
    (patch: ParamPatch, options?: { resetPage?: boolean }) => {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          if (options?.resetPage) next.set('page', '1');

          const apply = (
            key: string,
            value: string | null | undefined,
            omitValues: string[] = [],
          ) => {
            if (value === null || value === undefined || value === '' || omitValues.includes(value)) {
              next.delete(key);
              return;
            }
            next.set(key, value);
          };

          if (patch.page !== undefined) next.set('page', String(patch.page));
          if (patch.limit !== undefined) next.set('limit', String(patch.limit));
          if (patch.sortBy !== undefined) next.set('sortBy', patch.sortBy);
          if (patch.sortOrder !== undefined) next.set('sortOrder', patch.sortOrder);
          if (patch.search !== undefined) apply('search', patch.search);
          if (patch.customerId !== undefined) apply('customerId', patch.customerId);
          if (patch.orderStatus !== undefined) apply('orderStatus', patch.orderStatus, ['all']);
          if (patch.paymentStatus !== undefined) apply('paymentStatus', patch.paymentStatus, ['all']);
          if (patch.paymentMethod !== undefined) apply('paymentMethod', patch.paymentMethod, ['all']);
          if (patch.dateFrom !== undefined) apply('dateFrom', patch.dateFrom);
          if (patch.dateTo !== undefined) apply('dateTo', patch.dateTo);

          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const resetAll = useCallback(() => {
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.delete('search');
        next.delete('customerId');
        next.delete('orderStatus');
        next.delete('paymentStatus');
        next.delete('paymentMethod');
        next.delete('dateFrom');
        next.delete('dateTo');
        next.delete('sortBy');
        next.delete('sortOrder');
        next.set('page', '1');
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  const hasActiveFiltersOrSort = useMemo(
    () =>
      state.search !== '' ||
      state.customerId !== '' ||
      state.orderStatus !== 'all' ||
      state.paymentStatus !== 'all' ||
      state.paymentMethod !== 'all' ||
      state.dateFrom !== '' ||
      state.dateTo !== '' ||
      state.sortBy !== DEFAULTS.sortBy ||
      state.sortOrder !== DEFAULTS.sortOrder,
    [state],
  );

  return { state, apiParams, patchParams, resetAll, hasActiveFiltersOrSort };
}
